import React, { useEffect, forwardRef, useImperativeHandle } from 'react';
import { TouchableWithoutFeedback } from 'react-native';
import PropTypes from 'prop-types';
import AdapterConfig from './utils/AdapterConfig';
import TraceInit from './TraceInit';

const TraceVisibleContainer = forwardRef((props, ref) => {
  const {
    pageName,
    traceId,
    traceData,
    children,
    scmv2,
    statSend = {
      getParam: () => ({ spm: '' }),
      visible: () => {},
      touch: () => {},
      view: () => {},
    },
    isCustomVisible = false,
  } = props;
  useImperativeHandle(ref, () => ({
    onVisible,
  }));
  const onVisible = () => {
    const operation = AdapterConfig.getTraceOperation(traceId);
    const parsedTraceData = AdapterConfig.getTraceData(pageName, traceId, {
      traceData,
      type: 'visible',
    });
    const { b, c, d, iid } = parsedTraceData;

    if (operation?.includes('visible')) {
      const spm = AdapterConfig.getSpm(statSend, { b, c, d });

      // 为了兼容我们自己的埋点上报接口
      if (!TraceInit.getInstance().isSpm) {
        statSend && statSend.visible(parsedTraceData);
      } else {
        statSend && statSend.visible(c, iid, { spm, scmv2 });
      }
    }
  };
  useEffect(() => {
    if (!isCustomVisible) {
      // 小程序端需要延迟处理，否则太多曝光请求会阻塞页面渲染
      if (process.env && process.env.MIX_ENV === 'mini') {
        const miniVisibleTime = setTimeout(() => {
          onVisible();
        }, 3000);
        return () => {
          clearTimeout(miniVisibleTime);
        };
      } else {
        onVisible();
      }
    }
  }, []);
  return children;
});

const TraceClickContainer = (props) => {
  const {
    pageName,
    traceId,
    traceData,
    onPress,
    children,
    scmv2,
    statSend = {
      getParam: () => ({ spm: '' }),
      visible: () => {},
      touch: () => {},
      view: () => {},
    },
    TouchCompt = TouchableWithoutFeedback,
    ...args
  } = props;
  const onPressFun = (event) => {
    // 这里需要重新计算，iid依赖的参数以点击时刻为准
    const operation = AdapterConfig.getTraceOperation(traceId);
    const parsedTraceData = AdapterConfig.getTraceData(pageName, traceId, {
      traceData,
      type: 'touch',
    });
    const { b, c, d, iid } = parsedTraceData;
    const spm = AdapterConfig.getSpm(statSend, { b, c, d }, true);

    if (operation?.includes('touch')) {
      // 为了兼容我们自己的埋点上报接口
      if (!TraceInit.getInstance().isSpm) {
        statSend && statSend.touch(parsedTraceData);
      } else {
        if (process.env && process.env.MIX_ENV === 'mini') {
          let touchTimer = setTimeout(() => {
            touchTimer = null;
            clearTimeout(touchTimer);
            statSend && statSend.touch(c, iid, { spm, scmv2 });
          }, 1000);
        } else {
          statSend && statSend.touch(c, iid, { spm, scmv2 });
        }
      }
    } else {
      console.error('埋点数据异常：', operation);
    }
    onPress && onPress(event, spm, scmv2); // 回传是为了解决url上spmref问题
  };
  return (
    <TouchCompt {...args} onPress={onPressFun}>
      {children}
    </TouchCompt>
  );
};

const TraceClickAndVisibleContainer = (props) => {
  return (
    <TraceVisibleContainer ref={props.traceRef} {...props}>
      <TraceClickContainer {...props} />
    </TraceVisibleContainer>
  );
};

const TraceContainer = (props) => {
  const {
    traceId,
    traceData,
    onPress,
    children,
    scmv2,
    pageName,
    statSend,
    TouchCompt,
    traceRef,
    isCustomVisible = false,
    ...args
  } = props;
  const operation = AdapterConfig.getTraceOperation(traceId);

  if (operation.includes('visible') && operation.includes('touch')) {
    return (
      <TraceClickAndVisibleContainer
        onPress={onPress}
        TouchCompt={TouchCompt}
        children={children}
        scmv2={scmv2}
        statSend={statSend}
        traceRef={traceRef}
        isCustomVisible={isCustomVisible}
        pageName={pageName}
        traceId={traceId}
        traceData={traceData}
        {...args}
      />
    );
  } else if (operation.includes('visible')) {
    return (
      <TraceVisibleContainer
        children={children}
        scmv2={scmv2}
        statSend={statSend}
        traceRef={traceRef}
        isCustomVisible={isCustomVisible}
        pageName={pageName}
        traceId={traceId}
        traceData={traceData}
      />
    );
  } else if (operation.includes('touch')) {
    return (
      <TraceClickContainer
        onPress={onPress}
        TouchCompt={TouchCompt}
        children={children}
        scmv2={scmv2}
        statSend={statSend}
        pageName={pageName}
        traceId={traceId}
        traceData={traceData}
        {...args}
      />
    );
  }

  // 纯曝光且traceid为空，如果传了onPress，就有问题。 使用情况不变，理论上应该重新启用type，不合config耦合，但是需要其他人更改
  if (onPress) {
    return <TouchCompt {...props}>{children}</TouchCompt>;
  }
  return children;
};

TraceContainer.propTypes = {
  traceId: PropTypes.string, // 根据traceid找到响应的配置config
  onPress: PropTypes.func, // 如果是点击的话，把原来的TouchableWithoutFeedback组件的onPress传过来
  children: PropTypes.node, // 该组件中包的dom, 将原封不动展示
  traceData: PropTypes.object, // 需要上报的数据，改参数将被传递给config中的func
  scmv2: PropTypes.string,
  pageName: PropTypes.string, // b位
  statSend: PropTypes.object, // 发送打点 具体看StatSend
  TouchCompt: PropTypes.object, //  TouchableWithoutFeedback
  traceRef: PropTypes.object, //  自定义曝光时机使用, 默认曝光的情况下不传
  isCustomVisible: PropTypes.bool, // 是否在第一次加载的时候曝光
};
TraceContainer.defaultProps = {
  traceId: '',
  traceData: {},
  statSend: {
    getParam: () => ({ spm: '' }),
    visible: () => {},
    touch: () => {},
    view: () => {},
  },
  TouchCompt: TouchableWithoutFeedback,
  isCustomVisible: false,
};

export default TraceContainer;

// 公共的组件使用到埋点组件，建议直接使用TraceClickAndVisibleContainer, TraceClickContainer, TraceVisibleContainer
// 没有bug但是使用情况变复杂了
// export { TraceContainer, TraceClickAndVisibleContainer, TraceClickContainer, TraceVisibleContainer };
